import React, { useMemo, useState } from 'react'
import { Button, Input, Typography } from '@fullscreendigital/carrefour-dex-fe'
import { TitleHeader } from '../../../../Common/TitleHeader'
import { Container, Grid, Paper, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material'
import { generatePath, useNavigate, useParams, useSearchParams } from "react-router-dom"
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { makeCoreGetRequest, makeCorePostRequest } from "../../../../../../../utils/api"
import { useNotification } from '../../../../../../../hooks'
import { formatErrorMessage } from '../../../../../../../utils/formatting'
import { URLS } from '../../../../constants'
import { AsyncAutocomplete } from '../../../../../../../components/core-ui/AsyncAutocomplete'
import { AsyncAutocompleteOption } from "../../../../../../core-ui/AsyncAutocomplete/AsyncAutocompleteDefinitions"

export type DSSupportTypeDTO = {
  id: number
  code: string
  name: string
}

export type DSReceptionSupportDTO = {
  id: number | null
  supportNumber: number
  dsSupportTypeId: number
}

const PackageSelection: React.FC = () => {
  const navigate = useNavigate()
  const { showNotification } = useNotification()
  const queryClient = useQueryClient()

  const { id: orderId } = useParams<{ id: string }>()
  const [sp] = useSearchParams()
  const supplierCode = sp.get('supplierCode')

  const [selected, setSelected] = useState<AsyncAutocompleteOption | null>(null)
  const [qty, setQty] = useState<string>('')
  const [sessionRows, setSessionRows] = useState<Array<{ id: number; label: string; qty: number }>>([])

  const listKey = useMemo(() => ['dsSupportTypeListActiveReturnable'], [])

  const { data: supports, isLoading } = useQuery<DSSupportTypeDTO[]>(
    listKey,
    async () => {
      return await makeCoreGetRequest('/shop-reception-mobile/ds-order/ds-support-type/list', null)
    },
    {
      retry: 1,
      onError: (err) => showNotification({ message: `Nu s-au putut încărca tipurile de ambalaje. ${formatErrorMessage(err)}`, type: 'error' })
    }
  )

  const options: AsyncAutocompleteOption[] = useMemo(
    () => supports?.map(s => ({ value: s.id, label: `${s.name} (${s.code})` })),
    [supports]
  )

  const saveMutation = useMutation<void, unknown, DSReceptionSupportDTO>({
    mutationFn: async (payload) => {
      return await makeCorePostRequest(`/shop-reception-mobile/ds-order/ds-support-traceability/add-support/${orderId}/${supplierCode}`, payload)
    },
    onSuccess: (_data, vars) => {
      showNotification({ message: 'Suport salvat', type: 'success' })
      const label = options.find(o => o.value === vars.dsSupportTypeId)?.label || String(vars.dsSupportTypeId)
      setSessionRows(prev => [{ id: vars.dsSupportTypeId, label, qty: vars.supportNumber }, ...prev])
      setSelected(null)
      setQty('')
      queryClient.invalidateQueries(['receptionPackageList', orderId, supplierCode])
    },
    onError: (err) => showNotification({ message: `Nu s-a putut salva suportul. ${formatErrorMessage(err)}`, type: 'error' })
  })

  const canConfirm = Boolean(selected) && Number(qty) > 0

  const handleSave = () => {
    if (!orderId || !supplierCode) {
      showNotification({ message: 'Lipsesc parametrii necesari (orderId/supplierCode).', type: 'error' })
      return
    }
    if (!selected) {
      showNotification({ message: 'Selectează un tip de ambalaj', type: 'warning' })
      return
    }
    const q = Number(qty)
    if (!Number.isFinite(q) || q <= 0) {
      showNotification({ message: 'Cantitatea trebuie să fie > 0', type: 'warning' })
      return
    }
    const payload: DSReceptionSupportDTO = {
      id: null,
      dsSupportTypeId: Number(selected.value),
      supportNumber: q,
    }
    saveMutation.mutate(payload)
  }

  if (isLoading) return <CircularProgress color="primary" style={{ margin: '40px auto' }} disableShrink />

  return (
    <Container>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <TitleHeader title="SELECȚIE AMBALAJE" />
        </Grid>

        <Grid item xs={12}>
          <Paper style={{ padding: 12 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <AsyncAutocomplete
                  variant="outlined"
                  name="supportType"
                  label="Tip ambalaj"
                  value={selected}
                  options={options}
                  onChange={(opt) => setSelected(opt)}
                />
              </Grid>
              <Grid item xs={12}>
                <Input
                  type="number"
                  variant="standard"
                  label="Cantitate"
                  value={qty}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQty((e.target as HTMLInputElement).value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSave() }}
                  width="100%"
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  label={saveMutation.isLoading ? 'Se salvează…' : 'Salvează suportul'}
                  onClick={handleSave}
                  disabled={!canConfirm || saveMutation.isLoading}
                  style={{ width: '100%' }}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {sessionRows.length > 0 && (
          <Grid item xs={12}>
            <Paper style={{ padding: 8 }}>
              <Typography variant="medium" component="p" style={{ margin: '8px 0 12px' }}>
                Suporturi adăugate în această sesiune
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Tip</strong></TableCell>
                    <TableCell align="right"><strong>Cantitate</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sessionRows?.map((r, idx) => (
                    <TableRow key={`${r.id}-${idx}`}>
                      <TableCell>{r.label}</TableCell>
                      <TableCell align="right">{r.qty}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </Grid>
        )}

        <Grid item xs={12}>
          <Button
            type="button"
            variant="outlined"
            label="Am terminat"
            onClick={() => navigate(generatePath(URLS.AUTH_SUPPLIERS_PACKAGE_CONFIRMATION, { id: orderId }) + `?supplierCode=${supplierCode}`)}
            style={{ width: '100%' }}
          />
        </Grid>
      </Grid>
    </Container>
  )
}

export default PackageSelection
